import React, { useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { RoutePrefixContext } from "../context/RoutePrefixContext";

const PrefixedLink = ({ to, onClick, isModuleSubdomain = false, moduleType = null, ...props }) => {
	const routePrefix = useContext(RoutePrefixContext);
	const navigate = useNavigate();

	/**
	 * Ensures URLs on module subdomains never contain the module path segment
	 * 
	 * @param {string} url - URL to sanitize
	 * @param {Object} moduleTypeInfo - Module type info containing module name
	 * @returns {string} - Sanitized URL
	 */
	const sanitizeUrl = (url, moduleTypeInfo) => {
		if (!isModuleSubdomain || !moduleTypeInfo?.module || typeof url !== 'string') {
			return url;
		}
		
		try {
			// Extract organization ID (first segment) from the path
			const pathParts = window.location.pathname.split('/').filter(Boolean);
			const orgUrl = pathParts[0];
			
			if (!orgUrl) return url;
			
			// Check all possible module path patterns and remove them
			const modulePath = moduleTypeInfo.module;
			
			// Common pattern: /orgUrl/modulePath/uniqueUrl -> /orgUrl/uniqueUrl
			const mainPattern = new RegExp(`^\\/${orgUrl}\\/${modulePath}\\/(.+)$`);
			if (mainPattern.test(url)) {
				return url.replace(mainPattern, `/${orgUrl}/$1`);
			}
			
			// Alternative pattern: /modulePath/uniqueUrl -> /orgUrl/uniqueUrl (prefixing)
			const altPattern = new RegExp(`^\\/(?:${modulePath})\\/(.+)$`);
			if (altPattern.test(url)) {
				return `/${orgUrl}/${url.replace(altPattern, '$1')}`;
			}
			
			// Fall back to orgUrl/uniqueUrl format for subdomain URLs when no match found
			// Extract the last path segment as uniqueUrl
			if (url.startsWith('/') && url !== `/${orgUrl}`) {
				const segments = url.split('/').filter(Boolean);
				if (segments.length > 0) {
					const uniqueUrl = segments[segments.length - 1];
					return `/${orgUrl}/${uniqueUrl}`;
				}
			}
		} catch (error) {
			console.error("Error sanitizing URL:", error);
		}
		
		return url;
	};

	const handleClick = (e) => {
		e.preventDefault();
		if (onClick) onClick(e);

		let finalTo = "";
		let hash = "";

		// If on subdomain, no routePrefix
		if (to.startsWith("#")) {
			hash = to.slice(1);
			finalTo = `${isModuleSubdomain ? "" : routePrefix}${to}`; // If on subdomain, no routePrefix
		} else {
			const [path, anchor] = to.split("#");
			hash = anchor;
			finalTo = path;
			
			// For module subdomains, don't use normal routePrefix
			if (isModuleSubdomain) {
				// Clean URL for module subdomains
				finalTo = sanitizeUrl(finalTo, moduleType);
			} else if (!finalTo.startsWith(routePrefix)) {
				finalTo = `${routePrefix}${finalTo.startsWith("/") ? "" : "/"}${finalTo}`;
			}
			
			if (hash) {
				finalTo = `${finalTo}#${hash}`;
			}
		}

		// Navigate to the adjusted link
		navigate(finalTo);
		if (hash) {
			setTimeout(() => {
				const element = document.getElementById(hash);
				if (element) {
					// Ermittle die Höhe der Navbar (hier: das erste "nav"-Element)
					const navbar = document.querySelector("nav");
					const navbarHeight = navbar ? navbar.offsetHeight : 0;
					const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
					window.scrollTo({
						top: elementPosition - navbarHeight,
						behavior: "smooth",
					});
				}
			}, 100);
		}
	};

	// Display correct hover URL based on context
	let displayTo = "";
	if (to.startsWith("#")) {
		displayTo = `${isModuleSubdomain ? "" : routePrefix}${to}`;
	} else {
		const [path, anchor] = to.split("#");
		
		// Apply URL sanitization for display URL too
		if (isModuleSubdomain) {
			displayTo = sanitizeUrl(path, moduleType);
		} else if (!path.startsWith(routePrefix)) {
			displayTo = `${routePrefix}${path.startsWith("/") ? "" : "/"}${path}`;
		} else {
			displayTo = path;
		}
		
		if (anchor) {
			displayTo = `${displayTo}#${anchor}`;
		}
	}

	return (
		<Link
			onClick={handleClick}
			to={displayTo}
			{...props}
		/>
	);
};

export default PrefixedLink;
